import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface GraphFullReference {
  marker: number;
  document_id: string;
  filename?: string | null;
  page?: number | null;
}

/** Walks rendered text children and replaces `[N]` substrings with a
 * <CitationPill> when N is in the references map. Unresolvable markers
 * stay as plain text. */
const renderTextWithCitations = (
  children: React.ReactNode,
  refsByMarker: Map<number, GraphFullReference>,
  projectId?: string,
): React.ReactNode => {
  if (refsByMarker.size === 0 || !projectId) return children;
  const visit = (node: React.ReactNode, key: string): React.ReactNode => {
    if (typeof node === "string") {
      return splitCitations(node, refsByMarker, projectId, key);
    }
    if (Array.isArray(node)) {
      return node.map((child, i) => visit(child, `${key}-${i}`));
    }
    return node;
  };
  return visit(children, "root");
};

const _CITATION_RE = /\[(\d+)\]/g;
const splitCitations = (
  text: string,
  refsByMarker: Map<number, GraphFullReference>,
  projectId: string,
  keyPrefix: string,
): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  _CITATION_RE.lastIndex = 0;
  while ((match = _CITATION_RE.exec(text)) !== null) {
    const marker = Number(match[1]);
    const ref = refsByMarker.get(marker);
    if (!ref) continue;
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <CitationPill
        key={`${keyPrefix}-c${i++}`}
        marker={marker}
        reference={ref}
        projectId={projectId}
      />,
    );
    last = match.index + match[0].length;
  }
  if (parts.length === 0) return text;
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const CitationPill: React.FC<{
  marker: number;
  reference: GraphFullReference;
  projectId: string;
}> = ({ marker, reference, projectId }) => {
  // Match the URL pattern standard-mode DocumentChip uses so the
  // documents-table effect auto-opens the PDF in the new tab. NOTE: the
  // projects view (views/projects.tsx:54) reads `?page=` as the projects-
  // list pagination cursor — using it here would land us on an empty
  // projects list. Use `docPage` instead and have documents-table forward
  // it to the PDF viewer's pageNumber.
  const params = new URLSearchParams({
    docId: reference.document_id,
    open: "true",
  });
  if (reference.page != null) params.set("docPage", String(reference.page));
  const href = `/projects/${projectId}/documents?${params.toString()}`;
  const tooltip = [reference.filename, reference.page != null ? `p. ${reference.page}` : null]
    .filter(Boolean)
    .join(" — ");
  // Use window.open rather than <Link> / <a> because Radix Dialog renders
  // via a portal outside the React Router provider tree — Link/anchor
  // navigation behaves inconsistently there. The citations modal uses the
  // same pattern (see question-row.tsx:showDocument).
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(href, "_blank");
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      title={tooltip || undefined}
      className="mx-0.5 inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-blue-700 align-baseline hover:bg-blue-100 hover:text-blue-900"
    >
      [{marker}]
    </button>
  );
};

/**
 * Renders the long-form `graph_full` markdown answer in a dialog. Triggered
 * from `ExtractedValueCell` when an answer carries
 * `graph_metadata.full_markdown`.
 *
 * Component map mirrors the one used in `chats/chat-message.tsx` so styling
 * stays consistent across markdown surfaces in the app.
 */
export const GraphFullAnswerDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variableName?: string;
  questionText?: string;
  markdown: string;
  references?: GraphFullReference[];
  projectId?: string;
}> = ({ open, onOpenChange, variableName, questionText, markdown, references, projectId }) => {
  const heading = variableName || questionText || "Graph Full Answer";
  const refsByMarker = React.useMemo(() => {
    const m = new Map<number, GraphFullReference>();
    for (const r of references ?? []) m.set(r.marker, r);
    return m;
  }, [references]);
  const transform = React.useCallback(
    (children: React.ReactNode) =>
      renderTextWithCitations(children, refsByMarker, projectId),
    [refsByMarker, projectId],
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="text-lg font-semibold">{heading}</DialogTitle>
          {questionText && variableName ? (
            <p className="mt-1 text-sm text-muted-foreground">{questionText}</p>
          ) : null}
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4 text-sm text-gray-900">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => (
                <p className="mb-2 last:mb-0">{transform(children)}</p>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold">{transform(children)}</strong>
              ),
              ul: ({ children }) => (
                <ul className="mb-2 ml-4 list-disc space-y-1 last:mb-0">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="mb-2 ml-4 list-decimal space-y-1 last:mb-0">{children}</ol>
              ),
              li: ({ children }) => <li className="pl-1">{transform(children)}</li>,
              h1: ({ children }) => (
                <h3 className="mb-1 mt-3 text-base font-bold first:mt-0">{transform(children)}</h3>
              ),
              h2: ({ children }) => (
                <h4 className="mb-1 mt-3 text-sm font-bold first:mt-0">{transform(children)}</h4>
              ),
              h3: ({ children }) => (
                <h5 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{transform(children)}</h5>
              ),
              code: ({ children, className }) => {
                const isBlock = className?.includes("language-");
                if (isBlock)
                  return (
                    <code className="mb-2 block rounded bg-black/5 p-2 text-xs last:mb-0">
                      {children}
                    </code>
                  );
                return (
                  <code className="rounded bg-black/5 px-1 py-0.5 text-xs">
                    {children}
                  </code>
                );
              },
              pre: ({ children }) => <pre className="mb-2 last:mb-0">{children}</pre>,
              blockquote: ({ children }) => (
                <blockquote className="mb-2 border-l-2 border-current/20 pl-3 italic last:mb-0">
                  {children}
                </blockquote>
              ),
              hr: () => <hr className="my-2 border-current/10" />,
              a: ({ href, children, ...props }) => {
                const linkClasses = "font-semibold text-blue-600 hover:underline";
                if (href)
                  return (
                    <Link to={href} className={linkClasses} {...props}>
                      {children}
                    </Link>
                  );
                return <span>{children}</span>;
              },
            }}
          >
            {markdown}
          </ReactMarkdown>
        </div>
      </DialogContent>
    </Dialog>
  );
};
